import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { IconAi, IconCancel } from '../Icon';
import { Portal, PortalNamespace } from '../Portal';
import { SrOnly } from '../SrOnly';
import Tag from '../Tag';
import QuickMenuSearch from './QuickMenuSearch';
import { animationDuration, CloseBtn, Header, Result, UiBlock, Window, Wrapper } from './wrapper';

interface QuickMenuProps {
  id?: string | null;
  children: React.ReactNode;
  loading?: boolean;
  useAi?: boolean;
  onClose: () => void;
  onSearch: (value: string) => void;
  onToggleAi?: () => void;
}

const QuickMenu = ({
  id,
  children,
  loading,
  useAi,
  onClose,
  onSearch,
  onToggleAi,
}: QuickMenuProps) => {
  const { t } = useTranslation(['common', 'quickSearch']);
  const [animate, setAnimate] = useState(true);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const onCloseAnimate = useCallback(() => {
    setAnimate(false);
    setTimeout(() => onClose(), animationDuration);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const placeholder = useAi
    ? `${t('search', { ns: 'common' })} ${t('withAi', { ns: 'quickSearch' })}`
    : t('search', { ns: 'common' });

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <CSSTransition
        nodeRef={nodeRef}
        in={animate}
        appear={true}
        timeout={animationDuration}
        classNames="global-search"
      >
        <Wrapper ref={nodeRef}>
          <UiBlock onClick={onCloseAnimate} title={t('closeModal', { ns: 'common' }) || ''} />
          <Window>
            <Header>
              <QuickMenuSearch
                placeholder={placeholder}
                autoFocus
                loading={loading}
                onChange={(e) => {
                  onSearch(e.currentTarget.value);
                }}
              />
              {useAi && (
                <Tag
                  label={t('ai.tag', { ns: 'quickSearch' })}
                  buttonSize="tiny"
                  onClose={() => onToggleAi && onToggleAi()}
                  icon={<IconAi size="1rem" />}
                />
              )}
              <CloseBtn onClick={onCloseAnimate} title={t('closeModal', { ns: 'common' }) || ''}>
                <SrOnly>{t('closeModal', { ns: 'common' })}</SrOnly>
                <IconCancel />
              </CloseBtn>
            </Header>
            {children && <Result>{children}</Result>}
          </Window>
        </Wrapper>
      </CSSTransition>
    </Portal>
  );
};

export default QuickMenu;
